"use client";

import { useState } from "react";
import Link from "@/components/locale-link";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ApaTense,
  buildSignalPhrases,
  laterMentionNames,
  parseAuthors,
  REPORTING_VERBS,
  SIGNAL_STANCES,
  SIGNAL_STYLES,
  type SignalInput,
  type SignalStance,
  type SignalStyle,
  type SignalUse,
} from "@/lib/tool-kit/essay/signal-phrase";

/** English strings. A translated content file overrides these through `ui`. */
export const SIGNAL_PHRASE_GENERATOR_STRINGS = {
  styleLabel: "Citation style",
  styles: { apa: "APA 7", mla: "MLA 9", chicago: "Chicago (notes)" },
  useLabel: "Are you quoting or paraphrasing?",
  uses: { quote: "Quotation", paraphrase: "Paraphrase" },
  stanceLabel: "What is the source doing?",
  stances: {
    neutral: "Reporting",
    argue: "Arguing",
    agree: "Agreeing",
    disagree: "Disagreeing",
    tentative: "Suggesting",
  },
  stanceHints: {
    neutral:
      "Report what the source says or found, without signaling a view of it.",
    argue: "The source takes a position that others could dispute.",
    agree: "The source supports, confirms, or concedes a view.",
    disagree: "The source rejects, disputes, or questions a view.",
    tentative: "The source puts an idea forward cautiously.",
  },
  tenseLabel: "APA tense",
  tenses: { past: "Past (argued)", perfect: "Present perfect (has argued)" },
  authorsLabel: "Author or authors",
  authorsPlaceholder: "e.g. Pam A. Mueller\nDaniel M. Oppenheimer",
  authorsHint: "One author per line, as “First Last” or “Last, First”.",
  organization:
    "The author is an organization — type its name as it reads in a sentence, e.g. “the World Health Organization”",
  yearLabel: "Year (APA)",
  yearPlaceholder: "e.g. 2014",
  pageLabel: "Page or paragraph (optional)",
  pagePlaceholder: "e.g. 23, 23–25, or para. 4",
  textLabels: {
    quote: "The quotation (optional)",
    paraphrase: "Your paraphrase (optional)",
  },
  textPlaceholders: {
    quote: "Paste the exact words — the generator adds the quotation marks.",
    paraphrase: "The idea in your own words, as a clause.",
  },
  tryExample: "Try an example",
  clear: "Clear",
  needAuthor: "Add an author to see signal phrases.",
  resultsHeading: "Signal phrases",
  resultsIntro:
    "Each pattern uses a different verb from the stance you chose — honest templates, no AI.",
  patterns: {
    that: "Signal phrase + “that”",
    comma: "Comma before the quotation",
    according: "“According to”",
    as: "“As … says”",
    end: "Signal phrase at the end",
    interrupt: "Interrupting a quotation (pattern)",
  },
  copy: "Copy",
  copied: "Copied!",
  laterMentions: "Later mentions in the same paper: {names}",
  apaPageNeeded:
    "APA needs a page number for every direct quotation. For a source without pages, give a paragraph number (para. 4) instead.",
  mlaNoPage:
    "No page? MLA leaves the number out for sources without page numbers, such as most web pages.",
  chicagoNote:
    "The page number goes in the footnote, not the sentence. Build the note with the ",
  chicagoNoteLink: "Chicago Footnote Generator",
  chicagoNoteAfter: ".",
  styleRules: {
    apa: "APA 7: the year goes in parentheses straight after the name, signal verbs are past or present perfect, and a direct quotation always takes a page: “(p. 23)” after the closing quotation mark, then the period.",
    mla: "MLA 9: the author's full name the first time, the surname after that, the present tense, and the page in parentheses with no “p.” — “(23)” — before the period.",
    chicago:
      "Chicago notes and bibliography: no citation in the sentence. The superscript note number follows the closing quotation mark and the period, and the source and page go in the note.",
  },
  inTextTipBefore: "Need the citation in parentheses instead? Use the ",
  inTextTipLink: "In-Text Citation Generator",
  inTextTipAfter: ".",
  bankHeading: "Reporting verbs and what they signal",
  bankIntro:
    "Every verb tells the reader how you see the source. Choose the one that matches what the source actually does — “refutes” claims it succeeded.",
  bankHead: ["Verb", "What it signals", "APA (past)", "MLA and Chicago"],
  verbNuances: {
    finds:
      "Reports a result from research — the usual verb for empirical findings.",
    reports: "Relays information or results without judging them.",
    shows: "Presents a result as established by the evidence.",
    notes: "Mentions a point, often in passing.",
    observes: "Points to something seen in data, a text, or the world.",
    states: "Presents a point plainly, with no shading.",
    explains: "Sets out reasons or a process the source makes clear.",
    writes: "The most neutral choice of all.",
    "points out": "Draws attention to something — and implies you accept it.",
    argues:
      "Advances a position with reasons; the standard choice for a debatable claim.",
    contends: "Like argues, but hints the position is contested.",
    maintains: "Holds a position firmly, often against opposition.",
    claims:
      "Reports an assertion; can suggest you doubt it, so use it with care.",
    asserts: "States something forcefully, sometimes without much support.",
    insists: "Repeats a claim against resistance — strong and slightly loaded.",
    emphasizes: "Gives one point special weight within the source's argument.",
    confirms: "Adds evidence that something already proposed is true.",
    agrees: "Shares a view held by another writer.",
    "supports the view": "Gives evidence or reasons in favor of a position.",
    acknowledges:
      "Accepts a point, sometimes one that cuts against the source's own case.",
    concedes: "Admits a point, often reluctantly.",
    "corroborates the finding": "Independently backs up an earlier result.",
    "disputes the claim": "Argues that a claim is wrong — firm but neutral.",
    "challenges the view": "Questions a widely held position.",
    counters: "Answers another writer's argument with one of its own.",
    "refutes the claim":
      "Proves a claim false. Use it only if the source succeeds; otherwise write disputes.",
    "rejects the idea": "Dismisses a position outright.",
    "questions whether":
      "Raises doubt without necessarily rejecting the claim.",
    objects: "Raises a specific objection to an argument or proposal.",
    suggests: "Puts an idea forward cautiously — common for interpretations.",
    proposes: "Offers an idea, model, or plan for others to consider.",
    speculates: "Goes beyond the evidence; signals a guess.",
    hypothesizes:
      "States a testable explanation, usually before the evidence is in.",
    implies: "Suggests without saying directly. Writers imply; readers infer.",
    "wonders whether": "Raises an open question rather than taking a side.",
  },
  words: {
    according: "According to",
    as: "As",
    noDate: "n.d.",
    pageBlank: "p. ___",
    blank: "___",
    openQuote: "“",
    closeQuote: "”",
    ellipsis: "…",
  },
  example: {
    authors: "Pam A. Mueller\nDaniel M. Oppenheimer",
    year: "2014",
    page: "",
    text: "students who type lecture notes tend to transcribe them word for word, which leads to shallower processing than writing notes by hand",
  },
} as const;

type Strings = typeof SIGNAL_PHRASE_GENERATOR_STRINGS;

const EMPTY_INPUT: SignalInput = {
  authors: "",
  organization: false,
  year: "",
  page: "",
  text: "",
};

const BADGE =
  "inline-flex w-fit rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs";

/** Plain text with the Chicago note number raised. */
function Phrase({ text }: { text: string }) {
  const [before, after] = text.split("¹");
  return (
    <p className="text-foreground text-sm leading-relaxed">
      {before}
      {after === undefined ? null : (
        <>
          <sup>1</sup>
          {after}
        </>
      )}
    </p>
  );
}

function VerbBank({ strings }: { strings: Strings }) {
  return (
    <section className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.bankHeading}</h2>
      <p className="text-muted-foreground text-sm">{strings.bankIntro}</p>
      {SIGNAL_STANCES.map((stance) => (
        <div className="space-y-2" key={stance}>
          <h3 className={WIDGET_H3}>{strings.stances[stance]}</h3>
          <DataTable
            head={strings.bankHead}
            rows={REPORTING_VERBS[stance].map((entry) => [
              entry.id,
              strings.verbNuances[entry.id as keyof Strings["verbNuances"]] ??
                "",
              entry.past,
              entry.id,
            ])}
          />
        </div>
      ))}
    </section>
  );
}

function StyleNotice({
  style,
  use,
  page,
  strings,
}: {
  style: SignalStyle;
  use: SignalUse;
  page: string;
  strings: Strings;
}) {
  if (style === "chicago") {
    return (
      <Notice>
        {strings.chicagoNote}
        <Link
          className="text-primary underline underline-offset-4"
          href="/tools/chicago-footnote-generator"
        >
          {strings.chicagoNoteLink}
        </Link>
        {strings.chicagoNoteAfter}
      </Notice>
    );
  }
  if (use !== "quote" || page.trim() !== "") {
    return null;
  }
  return (
    <Notice>
      {style === "apa" ? strings.apaPageNeeded : strings.mlaNoPage}
    </Notice>
  );
}

export default function SignalPhraseGeneratorWidget() {
  const strings = useToolUi(SIGNAL_PHRASE_GENERATOR_STRINGS);
  const [style, setStyle] = useState<SignalStyle>("apa");
  const [use, setUse] = useState<SignalUse>("quote");
  const [stance, setStance] = useState<SignalStance>("neutral");
  const [apaTense, setApaTense] = useState<ApaTense>("past");
  const [input, setInput] = useState<SignalInput>(EMPTY_INPUT);

  function update<K extends keyof SignalInput>(key: K, value: SignalInput[K]) {
    setInput((previous) => ({ ...previous, [key]: value }));
  }

  function loadExample() {
    setUse("paraphrase");
    setStance("neutral");
    setInput({ ...EMPTY_INPUT, ...strings.example });
  }

  const phrases = buildSignalPhrases(
    input,
    { style, stance, use, apaTense },
    strings.words
  );
  const names = parseAuthors(input.authors, input.organization);

  const options = <K extends string>(
    values: readonly K[],
    labels: Record<K, string>
  ) => values.map((value) => ({ value, label: labels[value] }));

  return (
    <>
      <div className={`${CARD} space-y-5`}>
        <PillGroup
          label={strings.styleLabel}
          name="spg-style"
          onChange={setStyle}
          options={options(SIGNAL_STYLES, strings.styles)}
          value={style}
        />
        <PillGroup
          label={strings.useLabel}
          name="spg-use"
          onChange={setUse}
          options={options(["quote", "paraphrase"] as const, strings.uses)}
          value={use}
        />
        <div className="space-y-2">
          <PillGroup
            label={strings.stanceLabel}
            name="spg-stance"
            onChange={setStance}
            options={options(SIGNAL_STANCES, strings.stances)}
            value={stance}
          />
          <p className={HINT}>{strings.stanceHints[stance]}</p>
        </div>
        {style === "apa" ? (
          <PillGroup
            label={strings.tenseLabel}
            name="spg-tense"
            onChange={setApaTense}
            options={options(["past", "perfect"] as const, strings.tenses)}
            value={apaTense}
          />
        ) : null}
        <div className="space-y-2">
          <TextAreaField
            hint={strings.authorsHint}
            id="spg-authors"
            label={strings.authorsLabel}
            onChange={(value) => update("authors", value)}
            placeholder={strings.authorsPlaceholder}
            prose
            rows={3}
            value={input.authors}
          />
          <Checkbox
            checked={input.organization}
            label={strings.organization}
            onChange={(value) => update("organization", value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="spg-year"
            label={strings.yearLabel}
            onChange={(value) => update("year", value)}
            placeholder={strings.yearPlaceholder}
            value={input.year}
          />
          <TextField
            id="spg-page"
            label={strings.pageLabel}
            onChange={(value) => update("page", value)}
            placeholder={strings.pagePlaceholder}
            value={input.page}
          />
        </div>
        <TextAreaField
          id="spg-text"
          label={strings.textLabels[use]}
          onChange={(value) => update("text", value)}
          placeholder={strings.textPlaceholders[use]}
          prose
          rows={3}
          value={input.text}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput(EMPTY_INPUT)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {phrases.length === 0 ? (
          <Notice>{strings.needAuthor}</Notice>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              {strings.resultsIntro}
            </p>
            <StyleNotice
              page={input.page}
              strings={strings}
              style={style}
              use={use}
            />
            {phrases.map((phrase) => (
              <div className={`${RESULT_CARD} space-y-2`} key={phrase.pattern}>
                <div className="flex items-center justify-between gap-3">
                  <span className={BADGE}>
                    {strings.patterns[phrase.pattern]}
                  </span>
                  <CopyButton
                    copiedLabel={strings.copied}
                    html={phrase.html}
                    label={strings.copy}
                    small
                    text={phrase.text}
                  />
                </div>
                <Phrase text={phrase.text} />
              </div>
            ))}
            {style === "apa" ? null : (
              <p className={HINT}>
                {t(strings.laterMentions, {
                  names: laterMentionNames(names, style),
                })}
              </p>
            )}
            <p className="text-muted-foreground text-sm">
              {strings.styleRules[style]}
            </p>
            <p className={HINT}>
              {strings.inTextTipBefore}
              <Link
                className="underline underline-offset-4 hover:text-foreground"
                href="/tools/in-text-citation-generator"
              >
                {strings.inTextTipLink}
              </Link>
              {strings.inTextTipAfter}
            </p>
          </>
        )}
      </section>

      <VerbBank strings={strings} />
    </>
  );
}
